var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { property } from "lit/decorators.js";
import { $markLoaded, $needsRender, $onModelLoad, $progressTracker, $scene, $shouldAttemptPreload, $tick, $updateSource, $updateStatus, } from "../model-viewer-base.js";
import { $updateEnvironment } from "./environment.js";
import { ImageAlphaMode, ImageProjection, } from "../three-components/ImageObject.js";
import { isImageUrl } from "../utilities/content-type-detector.js";
import { clamp } from "../utilities.js";
const $imageObject = Symbol("imageObject");
/**
 * ImageMixin adds support for loading and displaying 2D images in model-viewer.
 * Images are automatically detected by file extension.
 */
export const ImageMixin = (ModelViewerElement) => {
    var _a;
    class ImageModelViewerElement extends ModelViewerElement {
        constructor() {
            super(...arguments);
            this.imageProjection = ImageProjection.Flat;
            this.imageAlphaMode = ImageAlphaMode.Opaque;
            this.imageAlphaCutoff = 0.5;
            this.mediumModelSrc = null;
            this.frameModelSrc = null;
            this[_a] = null;
        }
        updated(changedProperties) {
            super.updated(changedProperties);
            // Update image properties if they changed
            if (this[$imageObject] &&
                (changedProperties.has("imageProjection") ||
                    changedProperties.has("imageAlphaMode") ||
                    changedProperties.has("imageAlphaCutoff"))) {
                this[$imageObject].projection = this.imageProjection;
                this[$imageObject].alphaMode = this.imageAlphaMode;
                this[$imageObject].alphaCutoff = this.imageAlphaCutoff;
                this[$needsRender]();
            }
            if (!changedProperties.has("src") &&
                (changedProperties.has("mediumModelSrc") || changedProperties.has("frameModelSrc"))) {
                const source = this.src;
                const scene = this[$scene];
                if (source && isImageUrl(source) && source === scene.url) {
                    void scene.setImageSource(source, () => { }, true).then(() => {
                        var _b;
                        this[$imageObject] = (_b = scene.imageObject) !== null && _b !== void 0 ? _b : null;
                        this[$needsRender]();
                    }).catch((error) => {
                        this.dispatchEvent(new CustomEvent("error", {
                            detail: { type: "loadfailure", sourceError: error },
                        }));
                    });
                }
            }
        }
        async [(_a = $imageObject, $updateSource)]() {
            var _b;
            const scene = this[$scene];
            const source = this.src;
            // Check if the source is an image
            const isImage = source ? isImageUrl(source) : false;
            if (isImage) {
                // Handle image loading
                if (this.loaded ||
                    !this[$shouldAttemptPreload]() ||
                    source === scene.url) {
                    return;
                }
                if (this.generateSchema) {
                    scene.updateSchema(source);
                }
                this[$updateStatus]("Loading");
                const updateSourceProgress = this[$progressTracker].beginActivity("image-load");
                try {
                    const srcUpdated = scene.setImageSource(source, (progress) => updateSourceProgress(clamp(progress, 0, 1) * 0.95));
                    const envUpdated = this[$updateEnvironment]();
                    await Promise.all([srcUpdated, envUpdated]);
                    this[$imageObject] = (_b = scene.imageObject) !== null && _b !== void 0 ? _b : null;
                    this[$markLoaded]();
                    this[$onModelLoad]();
                    this.updateComplete.then(() => {
                        this.dispatchEvent(new CustomEvent("before-render"));
                    });
                    // Wait for shaders to compile and pixels to be drawn.
                    await new Promise((resolve) => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                this.dispatchEvent(new CustomEvent("load", { detail: { url: source } }));
                                resolve();
                            });
                        });
                    });
                }
                catch (error) {
                    this[$imageObject] = null;
                    this.dispatchEvent(new CustomEvent("error", {
                        detail: { type: "loadfailure", sourceError: error },
                    }));
                }
                finally {
                    updateSourceProgress(1.0);
                }
            }
            else {
                // Fall back to default model loading
                this[$imageObject] = null;
                return super[$updateSource]();
            }
        }
        [$tick](time, delta) {
            var _b;
            super[$tick](time, delta);
            (_b = this[$imageObject]) === null || _b === void 0 ? void 0 : _b.update();
        }
    }
    __decorate([
        property({ type: String, attribute: "image-projection" })
    ], ImageModelViewerElement.prototype, "imageProjection", void 0);
    __decorate([
        property({ type: String, attribute: "image-alpha-mode" })
    ], ImageModelViewerElement.prototype, "imageAlphaMode", void 0);
    __decorate([
        property({ type: Number, attribute: "image-alpha-cutoff" })
    ], ImageModelViewerElement.prototype, "imageAlphaCutoff", void 0);
    __decorate([
        property({ type: String, attribute: "medium-model-src", reflect: true })
    ], ImageModelViewerElement.prototype, "mediumModelSrc", void 0);
    __decorate([
        property({ type: String, attribute: "frame-model-src", reflect: true })
    ], ImageModelViewerElement.prototype, "frameModelSrc", void 0);
    return ImageModelViewerElement;
};
//# sourceMappingURL=image.js.map